import { getProviderProfile } from "./profiles";
// `isPointProgram` lives in `./rewards` since 2026-09-24, beside the redemption-type rules that
// also ask it (`redeemsAsRewards`). One definition; this file imported that one already.
import { isPointProgram, isPointsRedemption } from "./rewards";
import type { LoyaltyProviderProfile } from "./profiles";
import type { LoyaltyProvider, ProgramDetailsEnvelope } from "./types";

/**
 * Provider-level rules: what the provider in play permits.
 *
 * 🔴 **No rule here tests a `metaId`.** Each one asks that provider's capability profile
 * (`./profiles`) and applies a rule that is the same for every provider. Adding a partner is a
 * profile; changing what a capability MEANS is a rule here. Keeping those two separate is what
 * stops the flow re-accumulating `if (metaId === …)`.
 *
 * Pure — arguments in, answer out. Nothing here reads the store.
 */

/** Re-exported so a caller with a `metaId` can read any capability without a second import. */
export { getProviderProfile };
export type { LoyaltyProviderProfile };

/**
 * Does this provider publish a per-channel loyalty programme?
 *
 * 🔴 **Pair every programme read with this.** `programDetailsMap` is keyed by CHANNEL, not by
 * provider, so a cached programme outlives a provider change and can be read while a provider
 * that has no programme at all is active. Rules that forget the pairing hand one partner's
 * configuration to another.
 *
 * Store reads are paired already — `selectLoyaltyProgramDetails` asks this — so the rule is for a
 * function handed an envelope as an argument.
 */
export const providerPublishesProgram = (providerMetaId?: string): boolean =>
  getProviderProfile(providerMetaId).publishesProgram;

/**
 * Whether the POS may offer points redemption for this provider.
 *
 * Two conditions, both declared per provider: the partner must support redemption at all, and —
 * where it publishes a programme — that programme must be live, of the right type, and have POS
 * burning enabled. Every one of those is a merchant setting that can be off.
 *
 * 🔴 **The single gate for mounting the redemption panel.** It used to be applied at ONE of the
 * panel's two mount points, which left the tablet layout rendering it for providers the desktop
 * layout hid it for. `useCanRedeemPoints` is the hook form and both surfaces call it.
 */
export const canRedeemPointsOnPos = (
  providerMetaId?: string,
  programDetails?: ProgramDetailsEnvelope | null,
): boolean => {
  if (!providerMetaId) return false;
  const profile = getProviderProfile(providerMetaId);
  if (!profile.supportsPointsRedemption) return false;

  if (profile.requiresLiveProgramToRedeem) {
    const hasValidProgramDetails = !!programDetails?.data && !!programDetails.status;
    const isRedeemableProgramType = isPointProgram(programDetails);
    const isPosBurningEnabled = !!programDetails?.data?.burningSurfaces?.pos;
    if (!hasValidProgramDetails || !isRedeemableProgramType || !isPosBurningEnabled) return false;
    // 🔴 A `rista_deal` programme spends the SAME balance through milestone reward rows. Mounting
    // the panel as well offers those points twice and lets a cashier burn them on both — see
    // `./rewards`. Added 2026-09-09 (DM-146); the panel had no redemption-type gate before it.
    //
    // ⚠️ This refuses at ALL FOUR mount points — web `ChargableMethods`, web `MobileComponent`,
    // mobile `ChargableMethods`, and the kiosk's `useKioskPointsRedemption` — while the reward
    // rows that should replace the panel exist on WEB ONLY so far. So a `rista_deal` merchant has
    // no loyalty redemption UI on mobile or kiosk today. That is the known gap those platforms
    // close by MOUNTING THE ROWS (both are planned); it is not closed by relaxing this line, which
    // would put back a panel offering to spend points the programme does not spend that way.
    if (!isPointsRedemption(programDetails)) return false;
  }
  return true;
};

/** How many digits this provider's redemption OTP has — for the prompt, not for validation. */
export const getRedeemOtpLength = (providerMetaId?: string): number =>
  getProviderProfile(providerMetaId).redeemOtpLength;

/**
 * The `fetch` argument for `sale/customer/loyalty`, or `undefined` to omit the key.
 *
 * `undefined` is dropped by `JSON.stringify`, so a provider with no mode sends no key at all.
 */
export const getLoyaltyDetailsFetchMode = (providerMetaId?: string): string | undefined =>
  getProviderProfile(providerMetaId).loyaltyDetailsFetchMode;

/**
 * Should the programme be fetched for this provider?
 *
 * The fetch is per channel and only a programme-publishing provider has one to fetch; asking for
 * anyone else's is a guaranteed miss. Dispatched on channel selection — see
 * `useShouldFetchProgramDetails`.
 */
export const shouldFetchProgramDetails = (providerMetaId?: string): boolean =>
  providerPublishesProgram(providerMetaId);

/**
 * The membership or tier status this provider exposes on the customer's `loyaltyInfo`, or
 * `undefined`.
 *
 * Providers name this differently (or not at all), so the FIELD is per provider and the read is
 * one rule. A provider with no such concept answers `undefined` and its surfaces render nothing.
 */
export const getMembershipStatus = (
  providerMetaId: string | undefined,
  customerLoyaltyInfo: Record<string, any> | null | undefined,
): string | undefined => {
  const field = getProviderProfile(providerMetaId).membershipStatusField;
  if (!field) return undefined;
  return customerLoyaltyInfo?.[field] || undefined;
};

/** Is a provider object actually a provider? The store holds `{}` for "none selected". */
export const isProviderSelected = (provider?: LoyaltyProvider | null): boolean =>
  !!provider && !!provider.mid;
